import { supabase } from '../config/supabase.js'

// OneSignal Configuration
const ONE_SIGNAL_APP_ID = import.meta.env.VITE_ONE_SIGNAL_APP_ID

// Initialize OneSignal with fallback to browser notifications
export const initializeOneSignal = async () => {
  if (typeof window !== 'undefined' && window.OneSignal && location.protocol === 'https:') {
    try {
      await window.OneSignal.init({
        appId: ONE_SIGNAL_APP_ID,
        notifyButton: {
          enable: true,
          position: 'bottom-right'
        },
        welcomeNotification: {
          title: "Welcome to Oxygen Gym!",
          message: "Thanks for subscribing to notifications!"
        },
        serviceWorkerParam: {
          scope: '/'
        }
      })

      // Set up notification event listeners
      window.OneSignal.on('notificationDisplay', function(event) {
        console.log('OneSignal notification displayed:', event);
      });

      // Show notification permission prompt
      await window.OneSignal.showNativePrompt();
      
      console.log('OneSignal initialized successfully');
    } catch (error) {
      console.error('Error initializing OneSignal:', error);
      // Fallback to browser notifications
      await initializeBrowserNotifications();
    }
  } else {
    console.warn('OneSignal SDK not loaded, using browser notifications');
    await initializeBrowserNotifications();
  }
}

// Fallback browser notification system
export const initializeBrowserNotifications = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      const permission = await Notification.requestPermission();
      console.log('Browser notification permission:', permission);
    } catch (error) {
      console.error('Error requesting browser notification permission:', error);
    }
  }
}

// Subscribe user to notifications
export const subscribeToNotifications = async () => {
  try {
    if (typeof window !== 'undefined' && window.OneSignal) {
      // Wait for OneSignal to be fully initialized
      await window.OneSignal.isPushNotificationsEnabled()
      const playerId = await window.OneSignal.getUserId()
      console.log('User subscribed with player ID:', playerId)
      return playerId
    }
  } catch (error) {
    console.error('Error subscribing to notifications:', error)
    return null
  }
}

// Send push notification with fallback to browser notifications
export const sendPushNotification = async (title, message, data = {}) => {
  try {
    // First store the notification in database
    const { data: notification, error } = await supabase
      .from('push_notifications')
      .insert({
        title,
        message,
        data,
        sent_at: new Date().toISOString()
      })

    if (error) throw error

    // Try OneSignal first
    if (typeof window !== 'undefined' && window.OneSignal) {
      try {
        // For local testing, we'll use browser notifications as fallback
        if (Notification.permission === 'granted') {
          new Notification(title, {
            body: message,
            icon: '/icon-192x192.svg',
            tag: 'oxygen-gym',
            data: data,
            requireInteraction: true
          })
          console.log('Browser notification sent successfully')
        }
      } catch (oneSignalError) {
        console.warn('OneSignal notification failed, using browser fallback:', oneSignalError)
        // Fallback to browser notification
        if (Notification.permission === 'granted') {
          new Notification(title, {
            body: message,
            icon: '/icon-192x192.svg',
            tag: 'oxygen-gym',
            data: data,
            requireInteraction: true
          })
        }
      }
    } else {
      // Direct browser notification fallback
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: message,
          icon: '/icon-192x192.svg',
          tag: 'oxygen-gym',
          data: data,
          requireInteraction: true
        })
        console.log('Browser notification sent as fallback')
      } else if (Notification.permission !== 'denied') {
        // Request permission then send
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
          new Notification(title, {
            body: message,
            icon: '/icon-192x192.svg',
            tag: 'oxygen-gym',
            data: data,
            requireInteraction: true
          })
        }
      }
    }

    return notification
  } catch (error) {
    console.error('Error sending push notification:', error)
    throw error
  }
}

// Request notification permission
export const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission()
    return permission === 'granted'
  }
  return false
}

// Test OneSignal setup
export const testOneSignalSetup = async () => {
  if (typeof window !== 'undefined' && window.OneSignal) {
    try {
      const isPushSupported = await window.OneSignal.isPushNotificationsSupported()
      const isPushEnabled = await window.OneSignal.isPushNotificationsEnabled()
      const playerId = await window.OneSignal.getUserId()
      
      console.log('OneSignal Test Results:', {
        isSupported: isPushSupported,
        isEnabled: isPushEnabled,
        playerId: playerId
      })
      
      return {
        isSupported: isPushSupported,
        isEnabled: isPushEnabled,
        playerId: playerId
      }
    } catch (error) {
      console.error('OneSignal test failed:', error)
      return null
    }
  } else {
    console.warn('OneSignal not available')
    return null
  }
}